import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Eye, Image, CheckCircle, XCircle, Clock, Upload, Star, Camera, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { supabase, Gallery, GalleryPhoto, GalleryTag, GalleryTagRelation, getAuthenticatedStorage } from '@/lib/supabase';
import { TagManager } from '@/components/gallery/TagManager';
import { GradientText, CountingNumber, MotionHighlight } from '@/components/animate-ui';
import { LoadingCard, DataTableLoading } from '@/components/enhanced';

// Franchise regions with their display names
const franchiseRegions = [
  { value: 'HQ', label: 'Headquarters' },
  { value: 'north_york', label: 'North York' },
  { value: 'london', label: 'London' },
  { value: 'toronto', label: 'Toronto' },
  { value: 'hamilton', label: 'Hamilton' },
  { value: 'mississauga', label: 'Mississauga' },
  { value: 'brampton', label: 'Brampton' },
  { value: 'scarborough', label: 'Scarborough' },
  { value: 'general', label: 'General' }
] as const;

// Fetch galleries from Supabase
const fetchGalleries = async (): Promise<Gallery[]> => {
  const { data, error } = await supabase
    .from('galleries')
    .select('*')
    .order('created_on', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

// Fetch photos for a specific gallery
const fetchGalleryPhotos = async (galleryId: number): Promise<GalleryPhoto[]> => {
  const { data, error } = await supabase
    .from('gallery_photos')
    .select('*')
    .eq('gallery_id', galleryId)
    .order('sort_order', { ascending: true });
  
  if (error) throw error;
  return data || [];
};

// Create a new gallery
const createGallery = async (gallery: Omit<Gallery, 'id' | 'created_on'>): Promise<Gallery> => {
  const { data, error } = await supabase
    .from('galleries')
    .insert([gallery])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Update gallery
const updateGallery = async (id: number, updates: Partial<Gallery>): Promise<Gallery> => {
  const { data, error } = await supabase
    .from('galleries')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Delete gallery
const deleteGallery = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('galleries')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// Upload image to gallery
const uploadGalleryImage = async (galleryId: number, file: File, caption?: string): Promise<GalleryPhoto> => {
  const { storage } = await getAuthenticatedStorage();
  
  // Create unique filename
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
  const filePath = `galleries/${galleryId}/${fileName}`;
  
  // Upload file to Supabase Storage
  const { data: uploadData, error: uploadError } = await storage
    .from('gallery-images')
    .upload(filePath, file);
    
  if (uploadError) throw uploadError;
  
  // Get public URL
  const { data: urlData } = storage
    .from('gallery-images')
    .getPublicUrl(filePath);
    
  // Insert photo record
  const { data, error } = await supabase
    .from('gallery_photos')
    .insert({
      gallery_id: galleryId,
      image_url: urlData.publicUrl,
      caption: caption || '',
      sort_order: 0 // Will be updated based on existing photos
    })
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

// Delete gallery photo
const deleteGalleryPhoto = async (photoId: number): Promise<void> => {
  const { error } = await supabase
    .from('gallery_photos')
    .delete()
    .eq('id', photoId);
    
  if (error) throw error;
};

// Update photo caption
const updatePhotoCaption = async (photoId: number, caption: string): Promise<GalleryPhoto> => {
  const { data, error } = await supabase
    .from('gallery_photos')
    .update({ caption })
    .eq('id', photoId)
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

// Set cover image
const setCoverImage = async (galleryId: number, imageUrl: string): Promise<Gallery> => {
  const { data, error } = await supabase
    .from('galleries')
    .update({ cover_image: imageUrl })
    .eq('id', galleryId)
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

// Fetch all available tags
const fetchTags = async (): Promise<GalleryTag[]> => {
  const { data, error } = await supabase
    .from('gallery_tags')
    .select('*')
    .order('name', { ascending: true });
    
  if (error) {
    // If table doesn't exist, return empty array
    if (error.message.includes('relation "gallery_tags" does not exist')) {
      console.warn('Gallery tags table not found - tag functionality disabled');
      return [];
    }
    throw error;
  }
  return data || [];
};

// Fetch tags for a specific gallery
const fetchGalleryTags = async (galleryId: number): Promise<GalleryTag[]> => {
  const { data, error } = await supabase
    .from('gallery_tag_relations')
    .select(`
      gallery_tags (
        id,
        name,
        slug,
        description,
        created_at,
        updated_at
      )
    `)
    .eq('gallery_id', galleryId);
    
  if (error) {
    // If table doesn't exist, return empty array
    if (error.message.includes('relation "gallery_tag_relations" does not exist')) {
      return [];
    }
    throw error;
  }
  return data?.map(relation => relation.gallery_tags).filter(Boolean) || [];
};

// Create a new tag
const createTag = async (name: string, description?: string): Promise<GalleryTag> => {
  const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  
  const { data, error } = await supabase
    .from('gallery_tags')
    .insert({
      name,
      slug,
      description: description || ''
    })
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

// Add tag to gallery
const addTagToGallery = async (galleryId: number, tagId: string): Promise<void> => {
  const { error } = await supabase
    .from('gallery_tag_relations')
    .insert({
      gallery_id: galleryId,
      tag_id: tagId
    });
    
  if (error) throw error;
};

// Remove tag from gallery
const removeTagFromGallery = async (galleryId: number, tagId: string): Promise<void> => {
  const { error } = await supabase
    .from('gallery_tag_relations')
    .delete()
    .eq('gallery_id', galleryId)
    .eq('tag_id', tagId);
    
  if (error) throw error;
};

// Fetch all images from unified view for "All Images" tab
const fetchAllImages = async (page: number = 1, pageSize: number = 24) => {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  
  const { data, error, count } = await supabase
    .from('gallery_all_images')
    .select('*', { count: 'exact' })
    .range(from, to)
    .order('created_at', { ascending: false });
    
  if (error) throw error;
  
  return {
    images: data || [],
    totalCount: count || 0,
    page,
    pageSize
  };
};

interface GalleryFormData {
  title: string;
  description: string;
  franchise_region: Gallery['franchise_region'];
  review_status: 'pending' | 'approved' | 'rejected';
  is_public: boolean;
  used_for_social_media: boolean;
  job_number?: number;
  service_id?: string;
}

export default function GalleryManagement() {
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [managePhotosGallery, setManagePhotosGallery] = useState<number | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [editingCaption, setEditingCaption] = useState<{photoId: number, caption: string} | null>(null);
  const [activeTab, setActiveTab] = useState<'galleries' | 'all-images'>('galleries');
  const [allImagesPage, setAllImagesPage] = useState(1);
  const [allImagesPageSize] = useState(24);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<GalleryFormData>({
    title: '',
    description: '',
    franchise_region: 'general',
    review_status: 'pending',
    is_public: true,
    used_for_social_media: false,
    job_number: undefined,
    service_id: undefined
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch galleries
  const { data: galleries = [], isLoading, error } = useQuery({
    queryKey: ['galleries'],
    queryFn: fetchGalleries
  });

  // Fetch photos for selected gallery
  const { data: photos = [], refetch: refetchPhotos } = useQuery({
    queryKey: ['gallery-photos', managePhotosGallery],
    queryFn: () => fetchGalleryPhotos(managePhotosGallery!),
    enabled: !!managePhotosGallery
  });

  // Fetch all available tags
  const { data: allTags = [] } = useQuery({
    queryKey: ['gallery-tags'],
    queryFn: fetchTags,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Fetch tags for selected gallery
  const { data: galleryTags = [], refetch: refetchGalleryTags } = useQuery({
    queryKey: ['gallery-tags', selectedGallery?.id],
    queryFn: () => fetchGalleryTags(selectedGallery!.id),
    enabled: !!selectedGallery
  });

  // Fetch all images for "All Images" tab
  const { data: allImagesData, isLoading: isAllImagesLoading } = useQuery({
    queryKey: ['all-images', allImagesPage, allImagesPageSize],
    queryFn: () => fetchAllImages(allImagesPage, allImagesPageSize),
    enabled: activeTab === 'all-images'
  });

  // Create gallery mutation
  const createMutation = useMutation({
    mutationFn: createGallery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      setIsCreateDialogOpen(false);
      resetForm();
    }
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  // Update gallery mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<Gallery> }) => 
      updateGallery(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      setIsEditDialogOpen(false);
      setSelectedGallery(null);
      resetForm();
    }
  });

  // Delete gallery mutation
  const deleteMutation = useMutation({
    mutationFn: deleteGallery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
    }
  });

  // Upload image mutation
  const uploadImageMutation = useMutation({
    mutationFn: ({ galleryId, file, caption }: { galleryId: number; file: File; caption?: string }) =>
      uploadGalleryImage(galleryId, file, caption),
    onSuccess: () => {
      refetchPhotos();
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      toast({ title: 'Image uploaded successfully', message: 'The image has been added to your gallery', type: 'success' });
    },
    onError: (error) => {
      console.error('Upload error:', error);
      toast({ title: 'Upload failed', message: 'Please try again', type: 'error' });
    }
  });

  // Delete photo mutation
  const deletePhotoMutation = useMutation({
    mutationFn: deleteGalleryPhoto,
    onSuccess: () => {
      refetchPhotos();
      toast({ title: 'Photo deleted successfully', message: 'The image has been removed from your gallery', type: 'success' });
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast({ title: 'Delete failed', message: 'Please try again', type: 'error' });
    }
  });

  // Update caption mutation
  const updateCaptionMutation = useMutation({
    mutationFn: ({ photoId, caption }: { photoId: number; caption: string }) =>
      updatePhotoCaption(photoId, caption),
    onSuccess: () => {
      refetchPhotos();
      setEditingCaption(null);
      toast({ title: 'Caption updated successfully', message: 'The image caption has been saved', type: 'success' });
    }
  });

  // Set cover image mutation
  const setCoverImageMutation = useMutation({
    mutationFn: ({ galleryId, imageUrl }: { galleryId: number; imageUrl: string }) =>
      setCoverImage(galleryId, imageUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      toast({ title: 'Cover image updated successfully', message: 'This image is now the gallery cover', type: 'success' });
    }
  });

  // Create tag mutation
  const createTagMutation = useMutation({
    mutationFn: ({ name, description }: { name: string; description?: string }) =>
      createTag(name, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-tags'] });
      toast({ title: 'Tag created successfully', message: 'New tag is now available for use', type: 'success' });
    },
    onError: (error) => {
      console.error('Create tag error:', error);
      toast({ title: 'Failed to create tag', message: 'Please try again', type: 'error' });
    }
  });

  // Add tag to gallery mutation
  const addTagMutation = useMutation({
    mutationFn: ({ galleryId, tagId }: { galleryId: number; tagId: string }) =>
      addTagToGallery(galleryId, tagId),
    onSuccess: () => {
      refetchGalleryTags();
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      toast({ title: 'Tag added successfully', message: 'Tag has been associated with this gallery', type: 'success' });
    }
  });

  // Remove tag from gallery mutation
  const removeTagMutation = useMutation({
    mutationFn: ({ galleryId, tagId }: { galleryId: number; tagId: string }) =>
      removeTagFromGallery(galleryId, tagId),
    onSuccess: () => {
      refetchGalleryTags();
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      toast({ title: 'Tag removed successfully', message: 'Tag has been removed from this gallery', type: 'success' });
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      franchise_region: 'general',
      review_status: 'pending',
      is_public: true,
      used_for_social_media: false,
      job_number: undefined,
      service_id: undefined
    });
  };

  const handleViewDetails = (gallery: Gallery) => {
    navigate(`/media/galleries/${gallery.id}`);
  };

  const handleEdit = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setFormData({
      title: gallery.title,
      description: gallery.description || '',
      franchise_region: gallery.franchise_region,
      review_status: gallery.review_status,
      is_public: gallery.is_public,
      used_for_social_media: gallery.used_for_social_media || false,
      job_number: gallery.job_number,
      service_id: gallery.service_id
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedGallery) {
      updateMutation.mutate({
        id: selectedGallery.id,
        updates: formData
      });
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this gallery? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files || !managePhotosGallery) return;
    
    const fileArray = Array.from(files);
    setUploadingFiles(fileArray);
    
    // Upload each file
    fileArray.forEach(file => {
      uploadImageMutation.mutate({
        galleryId: managePhotosGallery,
        file,
        caption: ''
      });
    });
    
    // Clear uploading files after a delay
    setTimeout(() => setUploadingFiles([]), 2000);
  };

  const handleDeletePhoto = (photoId: number) => {
    if (confirm('Are you sure you want to delete this photo?')) {
      deletePhotoMutation.mutate(photoId);
    }
  };

  const handleSetCoverImage = (imageUrl: string) => {
    if (managePhotosGallery) {
      setCoverImageMutation.mutate({
        galleryId: managePhotosGallery,
        imageUrl
      });
    }
  };

  const handleCaptionEdit = (photoId: number, currentCaption: string) => {
    setEditingCaption({ photoId, caption: currentCaption || '' });
  };

  const handleCaptionSave = () => {
    if (editingCaption) {
      updateCaptionMutation.mutate({
        photoId: editingCaption.photoId,
        caption: editingCaption.caption
      });
    }
  };

  const handleManageTags = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setIsTagDialogOpen(true);
  };

  const handleToggleTag = (tagId: string) => {
    if (!selectedGallery) return;
    
    const isCurrentlySelected = galleryTags.some(tag => tag.id === tagId);
    
    if (isCurrentlySelected) {
      removeTagMutation.mutate({
        galleryId: selectedGallery.id,
        tagId
      });
    } else {
      addTagMutation.mutate({
        galleryId: selectedGallery.id,
        tagId
      });
    }
  };

  const handleCreateTag = (name: string, description?: string) => {
    createTagMutation.mutate({ name, description });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getRegionBadge = (region: string | undefined) => {
    if (!region) return null;
    const regionInfo = franchiseRegions.find(r => r.value === region);
    return <Badge variant="outline">{regionInfo?.label || region}</Badge>;
  };

  const stats = {
    total: galleries.length,
    approved: galleries.filter(g => g.review_status === 'approved').length,
    pending: galleries.filter(g => g.review_status === 'pending').length,
    socialMedia: galleries.filter(g => g.used_for_social_media).length
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <LoadingCard 
          variant="skeleton"
          title="Loading galleries..."
          showHeader={false}
          className="h-16"
        />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <LoadingCard
              key={i}
              variant="pulse"
              title="Loading stats..."
              showHeader={false}
              rows={1}
              className="h-24"
            />
          ))}
        </div>
        <LoadingCard 
          variant="skeleton"
          title="Loading gallery content..."
          rows={3}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Galleries</h2>
          <p className="text-gray-600">Unable to load gallery data. Please try again later.</p>
          <p className="text-sm text-gray-500 mt-2">Error: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div 
        className="flex justify-between items-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div>
          <h1 className="text-3xl font-bold">
            <GradientText 
              gradient="from-primary via-primary/90 to-primary/70"
              animate={false}
            >
              Gallery Management
            </GradientText>
          </h1>
          <motion.p 
            className="text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Manage photo galleries and collections
          </motion.p>
        </div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <MotionHighlight>
                <Button>
                  <motion.div
                    whileHover={{ rotate: 90, scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                    className="mr-2"
                  >
                    <Plus className="w-4 h-4" />
                  </motion.div>
                  Create Gallery
                </Button>
              </MotionHighlight>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Gallery</DialogTitle>
              <DialogDescription>
                Create a new photo gallery for your masonry projects
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="job_number">Job Number</Label>
                <Input
                  id="job_number"
                  type="number"
                  value={formData.job_number || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    job_number: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                />
              </div>
              
              <div>
                <Label htmlFor="franchise_region">Franchise Region</Label>
                <Select 
                  value={formData.franchise_region} 
                  onValueChange={(value: Gallery['franchise_region']) => 
                    setFormData(prev => ({ ...prev, franchise_region: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {franchiseRegions.map(region => (
                      <SelectItem key={region.value} value={region.value}>{region.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_public"
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: !!checked }))}
                />
                <Label htmlFor="is_public">Make gallery public</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="used_for_social_media"
                  checked={formData.used_for_social_media}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, used_for_social_media: !!checked }))}
                />
                <Label htmlFor="used_for_social_media">Use for social media</Label>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create Gallery'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </motion.div>
      </motion.div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          {
            title: 'Total Galleries',
            value: stats.total,
            icon: Image,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
          },
          {
            title: 'Approved',
            value: stats.approved,
            icon: CheckCircle,
            color: 'text-green-600',
            bgColor: 'bg-green-50',
          },
          {
            title: 'Pending Review',
            value: stats.pending,
            icon: Clock,
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-50',
          },
          {
            title: 'Social Media',
            value: stats.socialMedia,
            icon: Image,
            color: 'text-purple-600',
            bgColor: 'bg-purple-50',
          },
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 + (index * 0.1) }}
          >
            <MotionHighlight
              highlightClassName="bg-primary/5 border border-primary/20"
              className="h-full rounded-lg"
            >
              <Card className="border-0 shadow-sm transition-shadow duration-300 hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <motion.div
                      className={`p-2 rounded-lg ${stat.bgColor}`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <stat.icon className={`h-6 w-6 ${stat.color}`} />
                    </motion.div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <motion.p className="text-2xl font-bold">
                        <CountingNumber 
                          value={stat.value} 
                          duration={1.5}
                        />
                      </motion.p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </MotionHighlight>
          </motion.div>
        ))}
      </div>

      {/* Tabs for Galleries and All Images */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'galleries' | 'all-images')}>
          <MotionHighlight>
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="galleries" className="text-sm">
                Galleries ({stats.total})
              </TabsTrigger>
              <TabsTrigger value="all-images" className="text-sm">All Images ({allImagesData?.totalCount || 0})</TabsTrigger>
            </TabsList>
          </MotionHighlight>

        <TabsContent value="galleries" className="mt-6">
          {/* No Results Message */}
          {galleries.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Image className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No galleries found
                </h3>
                <p className="text-gray-600 mb-4">
                  Create your first gallery to get started
                </p>
              </CardContent>
            </Card>
          ) : (
            /* Galleries Grid */
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.9 }}
            >
              <AnimatePresence>
                {galleries.map((gallery, index) => (
                <motion.div
                  key={gallery.id}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 300,
                    damping: 25
                  }}
                  whileHover={{ 
                    y: -5,
                    transition: { duration: 0.2 }
                  }}
                >
                  <MotionHighlight highlightClassName="bg-primary/5 border border-primary/20">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewDetails(gallery)}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{gallery.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {getRegionBadge(gallery.franchise_region)}
                          {gallery.job_number && (
                            <Badge variant="outline" className="text-xs">
                              Job #{gallery.job_number}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(gallery.review_status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {gallery.cover_image && (
                      <div className="aspect-video bg-gray-100 rounded-lg mb-3 overflow-hidden">
                        <img
                          src={gallery.cover_image}
                          alt={gallery.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NSA2NEw5NSA3NEwxMTUgNTRMMTM1IDc0SDE1VjU0SDQ1VjY0SDg1WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                          }}
                        />
                      </div>
                    )}
                    
                    {gallery.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {gallery.description}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {gallery.is_public && (
                        <Badge variant="outline">Public</Badge>
                      )}
                      {gallery.used_for_social_media && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          Social Media
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        Created {new Date(gallery.created_on).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setManagePhotosGallery(gallery.id);
                          }}
                          title="Manage Photos"
                        >
                          <Camera className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(gallery);
                          }}
                          title="Edit Gallery"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {allTags.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleManageTags(gallery);
                            }}
                            title="Manage Tags"
                          >
                            <span className="text-sm font-bold">#</span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(gallery.id);
                          }}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                    </Card>
                  </MotionHighlight>
                </motion.div>
              ))}
              </AnimatePresence>
            </motion.div>
          )}
        </TabsContent>

        <TabsContent value="all-images" className="mt-6">
          {isAllImagesLoading ? (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {[...Array(12)].map((_, i) => (
                <motion.div 
                  key={i} 
                  className="aspect-square"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                >
                  <LoadingCard 
                    variant="skeleton" 
                    className="h-full" 
                    showHeader={false} 
                    rows={1}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : allImagesData?.images.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No images found</h3>
              <p className="text-gray-600">No images are available in the gallery collection</p>
            </div>
          ) : (
            <>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <AnimatePresence>
                  {allImagesData?.images.map((image, index) => (
                  <motion.div
                    key={`${image.image_type}_${image.original_id}`}
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.9 }}
                    transition={{ 
                      duration: 0.3, 
                      delay: index * 0.05,
                      type: "spring",
                      stiffness: 300,
                      damping: 25
                    }}
                    whileHover={{ 
                      y: -3,
                      scale: 1.02,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <MotionHighlight highlightClassName="bg-primary/5 border border-primary/20">
                      <Card className="hover:shadow-md transition-shadow h-full">
                    <CardContent className="p-4">
                      <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden relative group">
                        <img
                          src={image.image_url}
                          alt={image.caption || 'Gallery image'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NSA2NEw5NSA3NEwxMTUgNTRMMTM1IDc0SDE1VjU0SDQ1VjY0SDg1WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                          }}
                        />
                        
                        {/* Image Type Badge */}
                        <div className="absolute top-2 left-2">
                          <Badge variant={image.image_type === 'job_image' ? 'default' : 'secondary'} className="text-xs">
                            {image.image_type === 'job_image' ? 'Job Image' : 'Gallery Photo'}
                          </Badge>
                        </div>
                        
                        {/* Gallery Title Overlay */}
                        {image.gallery_title && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 rounded-b-lg">
                            {image.gallery_title}
                            {image.franchise_region && (
                              <span className="block text-gray-300">{image.franchise_region}</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <h3 className="font-medium text-sm truncate">{image.caption || 'Untitled'}</h3>
                      <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                        <span>{new Date(image.created_at).toLocaleDateString()}</span>
                        {image.job_number && (
                          <Badge variant="outline" className="text-xs">
                            Job #{image.job_number}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                      </Card>
                    </MotionHighlight>
                  </motion.div>
                ))}
                </AnimatePresence>
              </motion.div>

              {/* Pagination for All Images */}
              {allImagesData && Math.ceil(allImagesData.totalCount / allImagesPageSize) > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      Showing {((allImagesPage - 1) * allImagesPageSize) + 1} to {Math.min(allImagesPage * allImagesPageSize, allImagesData.totalCount)} of {allImagesData.totalCount} images
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAllImagesPage(prev => Math.max(1, prev - 1))}
                      disabled={allImagesPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {allImagesPage} of {Math.ceil(allImagesData.totalCount / allImagesPageSize)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAllImagesPage(prev => prev + 1)}
                      disabled={allImagesPage >= Math.ceil(allImagesData.totalCount / allImagesPageSize)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
        </Tabs>
      </motion.div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Gallery</DialogTitle>
            <DialogDescription>
              Fill in the details for your new gallery
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="create-title">Title</Label>
              <Input
                id="create-title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="create-description">Description</Label>
              <Textarea
                id="create-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="create-job_number">Job Number</Label>
              <Input
                id="create-job_number"
                type="number"
                value={formData.job_number || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  job_number: e.target.value ? parseInt(e.target.value) : undefined
                }))}
              />
            </div>
            
            <div>
              <Label htmlFor="create-region">Franchise Region</Label>
              <Select 
                value={formData.franchise_region} 
                onValueChange={(value: Gallery['franchise_region']) => 
                  setFormData(prev => ({ ...prev, franchise_region: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {franchiseRegions.map(region => (
                    <SelectItem key={region.value} value={region.value}>{region.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="create-status">Review Status</Label>
              <Select 
                value={formData.review_status} 
                onValueChange={(value: 'pending' | 'approved' | 'rejected') => 
                  setFormData(prev => ({ ...prev, review_status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="create-public"
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: !!checked }))}
              />
              <Label htmlFor="create-public">Make gallery public</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="create-social"
                checked={formData.used_for_social_media}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, used_for_social_media: !!checked }))}
              />
              <Label htmlFor="create-social">Use for social media</Label>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Gallery'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Gallery</DialogTitle>
            <DialogDescription>
              Update gallery information and settings
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="edit-job_number">Job Number</Label>
              <Input
                id="edit-job_number"
                type="number"
                value={formData.job_number || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  job_number: e.target.value ? parseInt(e.target.value) : undefined 
                }))}
              />
            </div>
            
            <div>
              <Label htmlFor="edit-region">Franchise Region</Label>
              <Select 
                value={formData.franchise_region} 
                onValueChange={(value: Gallery['franchise_region']) => 
                  setFormData(prev => ({ ...prev, franchise_region: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {franchiseRegions.map(region => (
                    <SelectItem key={region.value} value={region.value}>{region.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-status">Review Status</Label>
              <Select 
                value={formData.review_status} 
                onValueChange={(value: 'pending' | 'approved' | 'rejected') => 
                  setFormData(prev => ({ ...prev, review_status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-public"
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: !!checked }))}
              />
              <Label htmlFor="edit-public">Make gallery public</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-social"
                checked={formData.used_for_social_media}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, used_for_social_media: !!checked }))}
              />
              <Label htmlFor="edit-social">Use for social media</Label>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Updating...' : 'Update Gallery'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Photos Dialog */}
      <Dialog open={!!managePhotosGallery} onOpenChange={() => setManagePhotosGallery(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Gallery Photos</DialogTitle>
            <DialogDescription>
              {photos.length} photos in this gallery • Upload, edit, and organize your images
            </DialogDescription>
          </DialogHeader>
          
          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 mb-2">Drag and drop images here, or click to select files</p>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={!managePhotosGallery || uploadImageMutation.isPending}
            >
              {uploadImageMutation.isPending ? 'Uploading...' : 'Select Images'}
            </Button>
            {uploadingFiles.length > 0 && (
              <p className="text-xs text-blue-600 mt-2">Uploading {uploadingFiles.length} files...</p>
            )}
          </div>
          
          {photos.length === 0 ? (
            <div className="text-center py-8">
              <Image className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 mb-2">No photos in this gallery yet</p>
              <p className="text-sm text-gray-400">Upload your first image using the area above</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => {
                const currentGallery = galleries.find(g => g.id === managePhotosGallery);
                const isCoverImage = currentGallery?.cover_image === photo.image_url;
                
                return (
                  <div key={photo.id} className="relative group">
                    <img
                      src={photo.image_url}
                      alt={photo.caption || 'Gallery photo'}
                      className={`w-full h-32 object-cover rounded-lg transition-all ${
                        isCoverImage ? 'ring-2 ring-blue-500' : ''
                      }`}
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NSA2NEw5NSA3NEwxMTUgNTRMMTM1IDc0SDE1VjU0SDQ1VjY0SDg1WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                      }}
                    />
                    
                    {/* Cover Image Badge */}
                    {isCoverImage && (
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-blue-500 text-white text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          Cover
                        </Badge>
                      </div>
                    )}
                    
                    {/* Action Menu */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="secondary" size="sm" className="h-6 w-6 p-0">
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleSetCoverImage(photo.image_url)}>
                            <Star className="mr-2 h-4 w-4" />
                            Set as Cover
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCaptionEdit(photo.id, photo.caption || '')}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Caption
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Caption Display/Edit */}
                    {editingCaption?.photoId === photo.id ? (
                      <div className="absolute bottom-0 left-0 right-0 bg-white p-2 rounded-b-lg border-t">
                        <Input
                          value={editingCaption.caption}
                          onChange={(e) => setEditingCaption({ ...editingCaption, caption: e.target.value })}
                          placeholder="Add a caption..."
                          className="text-xs mb-2"
                          onKeyPress={(e) => e.key === 'Enter' && handleCaptionSave()}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleCaptionSave} disabled={updateCaptionMutation.isPending}>
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingCaption(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      photo.caption && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 rounded-b-lg">
                          {photo.caption}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gallery Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Eye className="w-5 h-5" />
              {selectedGallery?.title}
            </DialogTitle>
            <DialogDescription>
              Gallery details and photo overview
            </DialogDescription>
          </DialogHeader>
          
          {selectedGallery && (
            <div className="space-y-6">
              {/* Gallery Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-sm text-gray-700 mb-2">Gallery Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Status:</span>
                        {getStatusBadge(selectedGallery.review_status)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Region:</span>
                        {getRegionBadge(selectedGallery.franchise_region)}
                      </div>
                      {selectedGallery.job_number && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Job Number:</span>
                          <Badge variant="outline">#{selectedGallery.job_number}</Badge>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Created:</span>
                        <span className="text-sm text-gray-600">
                          {new Date(selectedGallery.created_on).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {selectedGallery.description && (
                    <div>
                      <h3 className="font-semibold text-sm text-gray-700 mb-2">Description</h3>
                      <p className="text-sm text-gray-600">{selectedGallery.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="font-semibold text-sm text-gray-700 mb-2">Settings</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">Public Gallery:</span>
                        <Badge variant={selectedGallery.is_public ? "default" : "outline"}>
                          {selectedGallery.is_public ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">Social Media Use:</span>
                        <Badge variant={selectedGallery.used_for_social_media ? "default" : "outline"}>
                          {selectedGallery.used_for_social_media ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Cover Image */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">Cover Image</h3>
                  {selectedGallery.cover_image ? (
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={selectedGallery.cover_image}
                        alt={selectedGallery.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDIwMCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTI4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04NSA2NEw5NSA3NEwxMTUgNTRMMTM1IDc0SDE1VjU0SDQ1VjY0SDg1WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <Image className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">No cover image set</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Tags Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-gray-700">Tags</h3>
                  {allTags.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageTags(selectedGallery)}
                    >
                      <span className="text-sm font-bold mr-1">#</span>
                      Manage Tags
                    </Button>
                  )}
                </div>
                {galleryTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {galleryTags.map(tag => (
                      <Badge key={tag.id} variant="outline" className="text-xs">
                        <span className="mr-1">#</span>
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {allTags.length > 0 ? 'No tags assigned' : 'Tag system not configured'}
                  </p>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    setIsDetailDialogOpen(false);
                    setManagePhotosGallery(selectedGallery.id);
                  }}
                  className="flex-1"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Manage Photos
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDetailDialogOpen(false);
                    handleEdit(selectedGallery);
                  }}
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Gallery
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tag Management Dialog */}
      <TagManager
        isOpen={isTagDialogOpen}
        onClose={() => setIsTagDialogOpen(false)}
        availableTags={allTags}
        selectedTags={galleryTags}
        onToggleTag={handleToggleTag}
        onCreateTag={handleCreateTag}
        isCreatingTag={createTagMutation.isPending}
      />
    </div>
  );
}